/**
 * Por qué no se puede borrar un registro: el texto para el usuario.
 *
 * El server no manda frases. Manda datos codificados (DeletionBlockers): la
 * entidad, el id y, por cada relación que bloquea, la entidad raíz, los nombres
 * que el usuario ya podía ver y cantidades. Acá se arma el texto en el idioma
 * del usuario, con las etiquetas que el generador emite por entidad en el
 * LangStore (<Entidad>Singular, <Entidad>Plural, <Entidad>Gender) y las frases
 * de LangStoreErrors (deletionBlockersMessages).
 *
 * Lo usan el diálogo de confirmación de las listas, antes de borrar, y el
 * procesador del error ENTITY_IN_USE (api/errorProcessors.ts), que llega si el
 * registro pasó a estar en uso entre la consulta y el Ok, si un formulario
 * quita una fila que otro usa, o si la base rechazó el borrado.
 *
 * Seguridad: solo se muestra lo que mandó el server. Los nombres salen
 * únicamente de `visible.items`; de lo demás, cantidades. Acá no se busca
 * ningún nombre.
 */
import type { LangStore } from "../../store/LangStore";
import {
  deletionBlockersMessages,
  DeletionBlockersPhrases,
  DeletionGender,
} from "../../store/LangStoreErrors";

export type DeletionBlockerName = string | { es: string; en: string; pt: string };

export type DeletionBlockerReason = {
  kind: "reference" | "audit";
  /** La entidad que el usuario conoce: la raíz del formulario que usa el registro. */
  rootEntity: string;
  /** "Modelo.campo" de cada relación por la que esta raíz usa el registro. */
  relations: string[];
  /** null: el usuario no puede leer la entidad raíz (o la red de seguridad, que no cuenta). */
  visible: { count: number; items: Array<{ id: string; name: DeletionBlockerName }> } | null;
  hiddenInTenant: number | null;
  otherTenants: number | null;
};

export type DeletionBlockers = {
  entity: string;
  id: string;
  blocked: boolean;
  reasons: DeletionBlockerReason[];
};

/** Nombres por motivo; el resto va como "y N más". Es el mismo tope del server. */
export const DELETION_BLOCKERS_MAX_NAMES = 5;

/** Lo que devuelve la consulta de src/common/schemas/deletionBlockers.graphql. */
export type DeletionBlockersQueryResult = {
  entity: string;
  id: string;
  blocked: boolean;
  reasons: Array<{
    kind: string;
    rootEntity: string;
    relations: string[];
    visible?: {
      count: number;
      items: Array<{ id: string; nameText?: string | null; nameI18n?: unknown }>;
    } | null;
    hiddenInTenant?: number | null;
    otherTenants?: number | null;
  }>;
};

type Lang = "es" | "en" | "pt";

const langOf = (langStore: LangStore): Lang => {
  const language = (langStore as unknown as { language?: unknown }).language;
  return language === "en" || language === "pt" ? language : "es";
};

/** Las frases de borrado en el idioma del usuario. */
export function deletionMessages(langStore: LangStore): DeletionBlockersPhrases {
  return deletionBlockersMessages()[langOf(langStore)];
}

const asI18nName = (value: unknown): { es: string; en: string; pt: string } | null => {
  let parsed = value;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return null;
    }
  }
  if (!parsed || typeof parsed !== "object") return null;
  const name = parsed as Record<string, unknown>;
  const text = (lang: Lang) => (typeof name[lang] === "string" ? (name[lang] as string) : "");
  if (!text("es") && !text("en") && !text("pt")) return null;
  return { es: text("es"), en: text("en"), pt: text("pt") };
};

/**
 * La consulta manda cada nombre como texto (`nameText`) o, si el nombre
 * visible de la raíz es multilingüe, como `{ es, en, pt }` (`nameI18n`). El
 * tipo de motivo se lee sin importar mayúsculas (por si el schema lo declara
 * como enum).
 */
export function deletionBlockersFromQuery(result: DeletionBlockersQueryResult): DeletionBlockers {
  return {
    entity: result.entity,
    id: result.id,
    blocked: result.blocked,
    reasons: (result.reasons ?? []).map((reason) => ({
      kind: String(reason.kind).toLowerCase() === "audit" ? "audit" : "reference",
      rootEntity: reason.rootEntity,
      relations: Array.isArray(reason.relations) ? reason.relations.map(String) : [],
      visible: reason.visible
        ? {
            count: reason.visible.count,
            items: (reason.visible.items ?? []).map((item) => ({
              id: item.id,
              name: asI18nName(item.nameI18n) ?? item.nameText ?? "",
            })),
          }
        : null,
      hiddenInTenant: reason.hiddenInTenant ?? null,
      otherTenants: reason.otherTenants ?? null,
    })),
  };
}

const entityLabels = (
  langStore: LangStore,
  entity: string,
  t: DeletionBlockersPhrases
): { singular: string; plural: string; gender: DeletionGender } => {
  const labels = langStore as unknown as Record<string, unknown>;
  // Solo se leen las etiquetas del LangStore; si falta alguna, una palabra genérica
  // y nunca el identificador que mandó el server.
  const text = (key: string) => {
    const value = entity ? labels[key] : undefined;
    return typeof value === "string" && value !== "" ? value : undefined;
  };
  const singular = text(`${entity}Singular`);
  return {
    singular: singular ?? t.record,
    plural: text(`${entity}Plural`) ?? (singular ? singular : t.records),
    gender: entity && String(labels[`${entity}Gender`] ?? "").toUpperCase() === "F" ? "F" : "M",
  };
};

/**
 * "Obra Musical" → "obra musical", "API Key" → "API key": pasa a minúscula cada
 * palabra "Capitalizada" (una mayúscula inicial y solo minúsculas, con acentos y ñ).
 * Las siglas ("DNDA", "LLM") y las palabras con mayúsculas internas ("iPhone") quedan.
 */
const lowerLabel = (label: string) =>
  label
    .split(/([^\p{L}]+)/u)
    .map((word) => (/^\p{Lu}\p{Ll}+$/u.test(word) ? word.toLowerCase() : word))
    .join("");

const joinList = (parts: string[], t: DeletionBlockersPhrases): string => {
  if (parts.length <= 1) return parts.join("");
  const last = parts[parts.length - 1];
  return `${parts.slice(0, -1).join(", ")}${t.and(last)}${last}`;
};

// ---------------------------------------------------------------------------
// La entrada se normaliza al entrar: este módulo no tira con ninguna forma. Lo
// que no tiene forma válida se descarta; las cuentas valen solo si son enteros
// finitos >= 0 (si no, null).
// ---------------------------------------------------------------------------

type CleanReason = {
  kind: "reference" | "audit";
  rootEntity: string;
  relations: string[];
  visible: { count: number; items: Array<{ id: string; name: string }> } | null;
  hidden: number | null;
  others: number | null;
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const str = (value: unknown): string => (typeof value === "string" ? value : "");

const count = (value: unknown): number | null =>
  typeof value === "number" && Number.isInteger(value) && value >= 0 ? value : null;

const cleanName = (name: unknown, lang: Lang): string => {
  if (typeof name === "string") return name;
  if (!isObject(name)) return "";
  return str(name[lang]) || str(name.es) || str(name.en) || str(name.pt);
};

const cleanReason = (reason: unknown, lang: Lang): CleanReason | null => {
  if (!isObject(reason)) return null;
  const rootEntity = str(reason.rootEntity);
  if (!rootEntity) return null;
  let visible: CleanReason["visible"] = null;
  if (isObject(reason.visible)) {
    const items: Array<{ id: string; name: string }> = [];
    for (const item of Array.isArray(reason.visible.items) ? reason.visible.items : []) {
      if (!isObject(item)) continue;
      const id = typeof item.id === "string" || typeof item.id === "number" ? String(item.id) : "";
      if (id) items.push({ id, name: cleanName(item.name, lang) });
    }
    visible = { count: count(reason.visible.count) ?? items.length, items };
  }
  return {
    kind: reason.kind === "audit" ? "audit" : "reference",
    rootEntity,
    relations: (Array.isArray(reason.relations) ? reason.relations : []).filter(
      (relation): relation is string => typeof relation === "string" && relation !== ""
    ),
    visible,
    hidden: count(reason.hiddenInTenant),
    others: count(reason.otherTenants),
  };
};

/** Un motivo de la red de seguridad: sabe la relación, no cuenta. */
const isUncounted = (reason: CleanReason) => reason.visible == null && reason.hidden == null && reason.others == null;

const modelOf = (relation: string) => relation.split(".")[0];

/** La auditoría suma todos sus motivos de la raíz: nunca nombres. */
const auditText = (reasons: CleanReason[], t: DeletionBlockersPhrases): string | null => {
  if (reasons.every(isUncounted)) return t.someForms;
  const total = reasons.reduce((sum, r) => sum + (r.visible?.count ?? 0) + (r.hidden ?? 0) + (r.others ?? 0), 0);
  if (total === 0) return null;
  const fields = new Set(reasons.flatMap((r) => r.relations.map((relation) => relation.split(".").pop())));
  if (fields.size === 1 && fields.has("createdBy_")) return t.createdBy(total);
  if (fields.size === 1 && fields.has("tenant_")) return t.ofTenant(total);
  return t.forms(total);
};

/**
 * El título y las líneas del texto. Las referencias se agrupan por entidad
 * raíz: los nombres visibles (los primeros 5, sin repetir), "y N más" y los
 * formularios de la empresa que el usuario no tiene a la vista; los de otras
 * empresas van sumados en una última línea. La auditoría (creador y empresa de
 * cada registro) va solo con cantidades.
 *
 * El server manda un motivo por tipo y entidad raíz, con las raíces distintas
 * ya contadas entre todas sus relaciones (relations). Si llegaran dos con la
 * misma raíz, van en una línea sin repetir nombres y con la cuenta mayor.
 *
 * Cuando la raíz es el mismo registro que se borra, se separa lo que es el
 * registro de lo que son otros: el propio id se saca de los nombres y de la
 * cuenta, y lo que queda se muestra como una línea normal. La línea "formularios
 * de X del propio registro" va solo si el propio id estaba entre los nombres o,
 * sin cuentas, si el modelo de la relación no es la entidad misma.
 */
export function deletionBlockersText(
  blockers: DeletionBlockers,
  langStore: LangStore,
  targetDisplay?: string
): { title: string; lines: string[] } {
  const lang = langOf(langStore);
  const t = deletionBlockersMessages()[lang];
  const source = isObject(blockers) ? (blockers as unknown as Record<string, unknown>) : {};
  const entity = str(source.entity);
  const id = typeof source.id === "string" ? source.id : "";
  const target = entityLabels(langStore, entity, t);
  const display = typeof targetDisplay === "string" && targetDisplay !== "" && targetDisplay !== id ? targetDisplay : undefined;
  const title = t.title(t.target(lowerLabel(target.singular), target.gender, display));
  const cleaned = (Array.isArray(source.reasons) ? source.reasons : [])
    .map((reason) => cleanReason(reason, lang))
    .filter((reason): reason is CleanReason => reason !== null);
  if (cleaned.length === 0) return { title, lines: [title] };

  // Los motivos cuya raíz es el mismo registro.
  const reasons: CleanReason[] = [];
  const ownModels: string[] = [];
  let ownCounted = false;
  for (const reason of cleaned) {
    if (reason.kind !== "reference" || !entity || reason.rootEntity !== entity) {
      reasons.push(reason);
      continue;
    }
    if (isUncounted(reason)) {
      const models = reason.relations.map(modelOf).filter((model) => model !== "" && model !== entity);
      if (models.length === 0) reasons.push(reason);
      else ownModels.push(...models);
      continue;
    }
    const items = reason.visible?.items ?? [];
    const found = id !== "" && items.some((item) => item.id === id);
    if (!found) {
      reasons.push(reason);
      continue;
    }
    ownCounted = true;
    ownModels.push(...reason.relations.map(modelOf).filter((model) => model !== ""));
    const rest = items.filter((item) => item.id !== id);
    const restCount = Math.max(rest.length, (reason.visible?.count ?? 0) - 1);
    if (restCount > 0 || (reason.hidden ?? 0) > 0 || (reason.others ?? 0) > 0) {
      reasons.push({ ...reason, visible: { count: restCount, items: rest } });
    }
  }
  const hasOwn = ownModels.length > 0 || ownCounted;
  let ownForms = "";
  if (hasOwn) {
    const labels = Array.from(new Set(ownModels)).map((model) => lowerLabel(entityLabels(langStore, model, t).singular));
    ownForms = t.ownForms(labels.length > 0 ? joinList(Array.from(new Set(labels)), t) : t.record);
    if (!ownCounted && reasons.length === 0) {
      return { title, lines: [t.usedByOwn(title, target.gender, ownForms)] };
    }
  }

  if (!hasOwn && reasons.every(isUncounted)) {
    const roots = Array.from(new Set(reasons.map((reason) => lowerLabel(entityLabels(langStore, reason.rootEntity, t).singular))));
    return { title, lines: [t.usedByUnknown(title, target.gender, joinList(roots, t))] };
  }

  const groups = new Map<string, { rootEntity: string; audit: boolean; reasons: CleanReason[] }>();
  for (const reason of reasons) {
    const audit = reason.kind === "audit";
    const key = `${audit ? "audit" : "reference"}:${reason.rootEntity}`;
    const group = groups.get(key) ?? { rootEntity: reason.rootEntity, audit, reasons: [] };
    group.reasons.push(reason);
    groups.set(key, group);
  }

  const lines = [t.usedBy(title, target.gender)];
  let otherTenants = 0;
  for (const group of Array.from(groups.values())) {
    const plural = entityLabels(langStore, group.rootEntity, t).plural;
    if (group.audit) {
      const text = auditText(group.reasons, t);
      if (text) lines.push(`• ${plural}: ${text}`);
      continue;
    }
    const seen = new Set<string>();
    const names: string[] = [];
    let visibleCount = 0;
    let hidden = 0;
    let others = 0;
    let uncounted = false;
    for (const reason of group.reasons) {
      if (isUncounted(reason)) {
        uncounted = true;
        continue;
      }
      if (reason.visible) {
        visibleCount = Math.max(visibleCount, reason.visible.count);
        for (const item of reason.visible.items) {
          if (seen.has(item.id)) continue;
          seen.add(item.id);
          if (item.name) names.push(item.name);
        }
      }
      hidden = Math.max(hidden, reason.hidden ?? 0);
      others = Math.max(others, reason.others ?? 0);
    }
    visibleCount = Math.max(visibleCount, seen.size);
    const shown = names.slice(0, DELETION_BLOCKERS_MAX_NAMES);
    const parts = [...shown];
    let kind: "plain" | "more" | "count" = "plain";
    if (visibleCount > shown.length) {
      if (shown.length > 0) {
        parts.push(t.more(visibleCount - shown.length));
        kind = "more";
      } else {
        parts.push(t.forms(visibleCount));
        kind = "count";
      }
    }
    // Con ocultos y solo nombres, los nombres van separados por comas y los ocultos cierran con una sola "y".
    let line = kind === "plain" && hidden > 0 ? parts.join(", ") : joinList(parts, t);
    if (hidden > 0) line = parts.length > 0 ? t.addHidden(line, hidden, kind) : t.hidden(hidden);
    else if (parts.length === 0 && uncounted) line = t.someForms;
    if (line) lines.push(`• ${plural}: ${line}`);
    otherTenants += others;
  }
  if (hasOwn) lines.push(`• ${ownForms.charAt(0).toUpperCase()}${ownForms.slice(1)}`);
  if (otherTenants > 0) lines.push(`• ${t.otherTenants(otherTenants)}`);
  return lines.length === 1 ? { title, lines: [title] } : { title, lines };
}
